import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { SessionUser } from '../../shared/contracts';
import { swipeDragOffset, swipeNavigationTarget } from '../../domain/swipe-navigation';
import { applyPreferredFontSize, getPreferredFontSize } from '../font-size';
import { PandaMark } from './PandaMark';

const swipeIgnoreSelector = 'button, a, input, select, textarea, label, summary, [role="button"], [role="radio"], [contenteditable], [role="dialog"], [data-swipe-ignore]';

export function Layout({ user, children, online }: { user: SessionUser; children: ReactNode; online: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pages = [
    { path: '/', icon: '⌂', label: 'ホーム', end: true },
    { path: '/events', icon: '▣', label: '予定' },
    { path: '/quick-add', icon: '＋', label: '予定を追加' },
    { path: '/mail-search', icon: '⌕', label: 'メール検索' },
    { path: '/settings', icon: '⚙', label: '設定' },
    ...(user.role === 'admin' ? [{ path: '/admin', icon: '♙', label: '管理' }] : []),
  ];
  const currentIndex = pages.findIndex((page) => page.path === location.pathname);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const gestureRef = useRef({ pointerId: -1, startX: 0, startY: 0, deltaX: 0, axis: '' as '' | 'horizontal' | 'vertical' });
  const pendingEntryRef = useRef<number | null>(null);
  const transitionTimerRef = useRef<number | null>(null);
  const entryFrameRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const suppressClickTimerRef = useRef<number | null>(null);

  useEffect(() => {
    applyPreferredFontSize(getPreferredFontSize(user.settings.fontSize));
  }, [user.settings.fontSize]);

  useLayoutEffect(() => {
    // Each navigation starts at its controls, except explicit section links.
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  useEffect(() => () => {
    if (transitionTimerRef.current) window.clearTimeout(transitionTimerRef.current);
    if (entryFrameRef.current) window.cancelAnimationFrame(entryFrameRef.current);
    if (suppressClickTimerRef.current) window.clearTimeout(suppressClickTimerRef.current);
  }, []);

  useLayoutEffect(() => {
    const direction = pendingEntryRef.current;
    if (direction === null) return;
    pendingEntryRef.current = null;
    setTransitioning(false);
    setSwipeOffset(direction > 0 ? window.innerWidth : -window.innerWidth);
    entryFrameRef.current = window.requestAnimationFrame(() => {
      entryFrameRef.current = window.requestAnimationFrame(() => {
        setTransitioning(true);
        setSwipeOffset(0);
        transitionTimerRef.current = window.setTimeout(() => setTransitioning(false), 220);
      });
    });
  }, [location.pathname]);

  const resetGesture = () => {
    gestureRef.current = { pointerId: -1, startX: 0, startY: 0, deltaX: 0, axis: '' };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.isPrimary === false) return;
    // A cancelled drag must never swallow the next tap, including on a control.
    if (suppressClickTimerRef.current) window.clearTimeout(suppressClickTimerRef.current);
    suppressClickRef.current = false;
    if (gestureRef.current.pointerId !== -1) {
      const previousPointerId = gestureRef.current.pointerId;
      if (event.currentTarget.hasPointerCapture(previousPointerId)) event.currentTarget.releasePointerCapture(previousPointerId);
      setSwiping(false);
      setSwipeOffset(0);
    }
    resetGesture();
    if (currentIndex < 0 || transitioning || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) return;
    if ((event.target as HTMLElement).closest(swipeIgnoreSelector)) return;
    gestureRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, deltaX: 0, axis: '' };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;
    if (gesture.pointerId !== event.pointerId || gesture.axis === 'vertical') return;
    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (!gesture.axis) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 8) return;
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        gesture.axis = 'vertical';
        return;
      }
      gesture.axis = 'horizontal';
      event.currentTarget.setPointerCapture(event.pointerId);
      setSwiping(true);
    }
    gesture.deltaX = deltaX;
    suppressClickRef.current ||= Math.abs(deltaX) > 12;
    setSwipeOffset(swipeDragOffset(currentIndex, pages.length, deltaX));
    event.preventDefault();
  };

  const finishSwipe = (event: ReactPointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;
    if (gesture.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const wasHorizontal = gesture.axis === 'horizontal';
    const targetIndex = wasHorizontal
      ? swipeNavigationTarget(currentIndex, pages.length, gesture.deltaX, window.innerWidth)
      : null;
    resetGesture();
    setSwiping(false);
    if (targetIndex === null) {
      setTransitioning(wasHorizontal);
      setSwipeOffset(0);
      if (wasHorizontal) transitionTimerRef.current = window.setTimeout(() => setTransitioning(false), 220);
    } else {
      const direction = targetIndex > currentIndex ? 1 : -1;
      setTransitioning(true);
      setSwipeOffset(direction > 0 ? -window.innerWidth : window.innerWidth);
      transitionTimerRef.current = window.setTimeout(() => {
        pendingEntryRef.current = direction;
        navigate(pages[targetIndex].path);
      }, 190);
    }
    suppressClickTimerRef.current = window.setTimeout(() => { suppressClickRef.current = false; }, 0);
  };

  const cancelSwipe = (event: ReactPointerEvent<HTMLElement>) => {
    if (gestureRef.current.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    suppressClickRef.current = false;
    if (suppressClickTimerRef.current) window.clearTimeout(suppressClickTimerRef.current);
    const wasHorizontal = gestureRef.current.axis === 'horizontal';
    resetGesture();
    setSwiping(false);
    if (wasHorizontal) {
      setTransitioning(true);
      setSwipeOffset(0);
      transitionTimerRef.current = window.setTimeout(() => setTransitioning(false), 220);
    }
  };

  const swipeStyle = swiping || transitioning || swipeOffset !== 0
    ? { transform: `translate3d(${swipeOffset}px, 0, 0)` }
    : undefined;

  return (
    <div className="app-shell">
      {!online && <div className="offline-banner">オフラインです。接続が戻ると最新情報を確認できます。</div>}
      <header className="topbar">
        <NavLink to="/" className="brand"><PandaMark size={38} /><span>めーるパンダ</span></NavLink>
        <div className="user-chip" title={user.email}>
          {user.avatarUrl ? <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" /> : <span>{user.displayName.slice(0, 1)}</span>}
          <span className="desktop-only">{user.displayName}</span>
        </div>
      </header>
      <main
        className={`main-content swipe-page ${swiping ? 'is-swiping' : ''} ${transitioning ? 'is-swipe-transitioning' : ''}`}
        style={swipeStyle}
        data-swipe-navigation={currentIndex >= 0 ? 'enabled' : 'disabled'}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishSwipe}
        onPointerCancel={cancelSwipe}
        onClickCapture={(event) => { if (suppressClickRef.current) { event.preventDefault(); event.stopPropagation(); } }}
      >{children}</main>
      <nav className="bottom-nav" aria-label="メインメニュー">
        {pages.map((page) => <NavLink key={page.path} to={page.path} end={page.end} className={page.path === '/quick-add' ? 'nav-add' : undefined}><span aria-hidden="true">{page.icon}</span>{page.label}</NavLink>)}
      </nav>
    </div>
  );
}
